"use client"

import { useState, useEffect } from "react"
import { useRouter } from "next/navigation"
import { Button } from "@/components/ui/button"
import { ArrowLeft, AlertCircle, Calendar, MapPin, Wind, CheckCircle } from "lucide-react"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { format } from "date-fns"

interface EnvironmentalComplaint {
  id: string
  complaintType: string
  location: string
  detectedAt: string
  windSpeed?: string
  windDirection?: string
  likelyCause?: string
  correctiveAction?: string
  response?: string
  responseDate?: string
  notifiedCouncil: boolean
  councilNotifiedAt?: string
  complainantName?: string
  complainantContact?: string
  additionalNotes?: string
  createdAt: string
}

export default function CouncilComplaintsPage() {
  const [isLoading, setIsLoading] = useState(true)
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [complaints, setComplaints] = useState<EnvironmentalComplaint[]>([])
  const router = useRouter()

  useEffect(() => {
    const checkAuth = async () => {
      const sessionToken = localStorage.getItem("councilSessionToken")
      
      if (!sessionToken) {
        router.push("/council/login")
        return
      }

      try {
        const response = await fetch("/api/council/verify", {
          headers: { "Authorization": `Bearer ${sessionToken}` },
        })

        if (!response.ok) {
          localStorage.removeItem("councilSessionToken")
          router.push("/council/login")
          return
        }

        setIsAuthenticated(true)
        
        // Fetch complaints
        const complaintsResponse = await fetch("/api/council/complaints", {
          headers: { "Authorization": `Bearer ${sessionToken}` },
        })

        if (complaintsResponse.ok) {
          const data = await complaintsResponse.json()
          setComplaints(data)
        }
        
        // Track page access
        try {
          await fetch("/api/council/activity", {
            method: "POST",
            headers: { 
              "Content-Type": "application/json",
              "Authorization": `Bearer ${sessionToken}`,
            },
            body: JSON.stringify({ 
              page: "/council/complaints",
              action: "council_complaints_view"
            }),
          })
        } catch (error) {
          console.error("Error tracking activity:", error)
        }
      } catch (error) {
        console.error("Auth verification error:", error)
        localStorage.removeItem("councilSessionToken")
        router.push("/council/login")
      } finally {
        setIsLoading(false)
      }
    }

    checkAuth()
  }, [router])

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#5b0861] mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    )
  }

  if (!isAuthenticated) {
    return null
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => router.push("/council/dashboard")}
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Dashboard
              </Button>
              <div>
                <h1 className="text-2xl font-bold text-[#5b0861]">PetAftercare</h1>
                <p className="text-sm text-gray-600">Environmental Complaints</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertCircle className="h-6 w-6 text-[#5b0861]" />
              Complaint Records (Condition 22, 23)
            </CardTitle>
            <CardDescription>
              Records of complaints relating to particulate matter or odour, including responses and Council notifications
            </CardDescription>
          </CardHeader>
        </Card>

        <div className="space-y-4">
          {complaints.length === 0 ? (
            <Card>
              <CardContent className="pt-6 text-center text-gray-500">
                No complaints recorded
              </CardContent>
            </Card>
          ) : (
            complaints.map((complaint) => (
              <Card key={complaint.id}>
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <CardTitle className="text-lg">
                          {complaint.complaintType === "odour" ? "Odour" : "Particulate Matter"} Complaint
                        </CardTitle>
                        {complaint.notifiedCouncil && (
                          <Badge className="bg-green-100 text-green-800">
                            <CheckCircle className="w-3 h-3 mr-1" />
                            Council Notified
                          </Badge>
                        )}
                        {complaint.response && (
                          <Badge variant="outline">Responded</Badge>
                        )}
                      </div>
                      <div className="flex items-center gap-4 text-sm text-gray-600">
                        <div className="flex items-center gap-1">
                          <Calendar className="w-4 h-4" />
                          {format(new Date(complaint.detectedAt), "PPp")}
                        </div>
                        <div className="flex items-center gap-1">
                          <MapPin className="w-4 h-4" />
                          {complaint.location}
                        </div>
                      </div>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    {complaint.windSpeed && (
                      <div>
                        <p className="text-xs text-gray-500 mb-1">Wind Speed</p>
                        <p className="text-sm font-medium">{complaint.windSpeed}</p>
                      </div>
                    )}
                    {complaint.windDirection && (
                      <div>
                        <p className="text-xs text-gray-500 mb-1">Wind Direction</p>
                        <p className="text-sm font-medium">{complaint.windDirection}</p>
                      </div>
                    )}
                    {complaint.complainantName && (
                      <div>
                        <p className="text-xs text-gray-500 mb-1">Complainant</p>
                        <p className="text-sm font-medium">{complaint.complainantName}</p>
                      </div>
                    )}
                    {complaint.complainantContact && (
                      <div>
                        <p className="text-xs text-gray-500 mb-1">Contact</p>
                        <p className="text-sm font-medium">{complaint.complainantContact}</p>
                      </div>
                    )}
                  </div>

                  {complaint.likelyCause && (
                    <div className="mb-3">
                      <p className="text-xs text-gray-500 mb-1">Likely Cause</p>
                      <p className="text-sm text-gray-700">{complaint.likelyCause}</p>
                    </div>
                  )}

                  {complaint.correctiveAction && (
                    <div className="mb-3">
                      <p className="text-xs text-gray-500 mb-1">Corrective Action</p>
                      <p className="text-sm text-gray-700">{complaint.correctiveAction}</p>
                    </div>
                  )}

                  {complaint.response && (
                    <div className="mb-3 p-3 bg-green-50 border border-green-200 rounded">
                      <p className="text-xs text-gray-500 mb-1">Response to Complainant</p>
                      <p className="text-sm text-gray-700">{complaint.response}</p>
                      {complaint.responseDate && (
                        <p className="text-xs text-gray-500 mt-1">
                          Responded: {format(new Date(complaint.responseDate), "PPp")}
                        </p>
                      )}
                    </div>
                  )}

                  {complaint.notifiedCouncil && complaint.councilNotifiedAt && (
                    <div className="mb-3 p-3 bg-blue-50 border border-blue-200 rounded">
                      <p className="text-xs text-gray-500 mb-1">Council Notification</p>
                      <p className="text-sm text-gray-700">
                        Notified Canterbury Regional Council on {format(new Date(complaint.councilNotifiedAt), "PPp")}
                      </p>
                    </div>
                  )}

                  {complaint.additionalNotes && (
                    <div className="mb-3">
                      <p className="text-xs text-gray-500 mb-1">Additional Notes</p>
                      <p className="text-sm text-gray-700">{complaint.additionalNotes}</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))
          )}
        </div>
      </div>
    </div>
  )
}
